import React, { ReactNode } from 'react';

interface DataTableProps {
  children: ReactNode;
  'aria-label'?: string;
  'aria-labelledby'?: string;
  className?: string;
}

/**
 * Reusable DataTable wrapper component for consistent table styling
 * Provides zebra striping, consistent borders, and accessibility features
 */
export const DataTable: React.FC<DataTableProps> = ({
  children,
  'aria-label': ariaLabel,
  'aria-labelledby': ariaLabelledBy,
  className = '',
}) => {
  return (
    <div className="overflow-x-auto">
      <table
        className={`w-full border-collapse text-xs bg-white border border-gray-300 ${className}`}
        role="table"
        aria-label={ariaLabel}
        aria-labelledby={ariaLabelledBy}
      >
        {children}
      </table>
    </div>
  );
};

interface TableHeaderProps {
  children: ReactNode;
  className?: string;
}

export const TableHeader: React.FC<TableHeaderProps> = ({ children, className = '' }) => {
  return <thead className={className}>{children}</thead>;
};

interface TableBodyProps {
  children: ReactNode;
  className?: string;
}

export const TableBody: React.FC<TableBodyProps> = ({ children, className = '' }) => {
  return <tbody className={className}>{children}</tbody>;
};

interface TableRowProps {
  children: ReactNode;
  index?: number;
  className?: string;
  'aria-label'?: string;
  'aria-pressed'?: boolean | 'mixed';
  'aria-selected'?: boolean;
  onClick?: () => void;
  role?: string;
}

/**
 * TableRow with automatic zebra striping
 */
export const TableRow: React.FC<TableRowProps> = ({
  children,
  index,
  className = '',
  'aria-label': ariaLabel,
  'aria-pressed': ariaPressed,
  'aria-selected': ariaSelected,
  onClick,
  role = 'row',
}) => {
  const zebraClass = index !== undefined ? (index % 2 === 0 ? 'bg-white' : 'bg-gray-50') : '';
  const hoverClass = onClick ? 'hover:bg-gray-100 cursor-pointer transition-colors' : '';

  return (
    <tr
      className={`border-b border-gray-200 ${zebraClass} ${hoverClass} ${className}`}
      role={role}
      aria-label={ariaLabel}
      aria-pressed={onClick && ariaPressed === undefined ? false : ariaPressed}
      aria-selected={ariaSelected}
      onClick={onClick}
      tabIndex={onClick ? 0 : undefined}
      onKeyDown={
        onClick
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onClick();
              }
            }
          : undefined
      }
    >
      {children}
    </tr>
  );
};

interface TableHeaderCellProps {
  children: ReactNode;
  colSpan?: number;
  rowSpan?: number;
  className?: string;
  scope?: 'col' | 'row' | 'colgroup' | 'rowgroup';
}

export const TableHeaderCell: React.FC<TableHeaderCellProps> = ({
  children,
  colSpan,
  rowSpan,
  className = '',
  scope = 'col',
}) => {
  return (
    <th
      className={`p-3.5 px-3 text-center font-bold text-sm text-gray-800 bg-gray-50 border-b-2 border-gray-300 uppercase tracking-wide ${className}`}
      colSpan={colSpan}
      rowSpan={rowSpan}
      scope={scope}
    >
      {children}
    </th>
  );
};

interface TableSubHeaderCellProps {
  children: ReactNode;
  colSpan?: number;
  rowSpan?: number;
  className?: string;
  scope?: 'col' | 'row' | 'colgroup' | 'rowgroup';
}

export const TableSubHeaderCell: React.FC<TableSubHeaderCellProps> = ({
  children,
  colSpan,
  rowSpan,
  className = '',
  scope = 'col',
}) => {
  return (
    <th
      className={`p-2.5 px-2 text-center font-semibold text-xs text-gray-700 bg-white border-b border-gray-300 border-r border-gray-200 ${className}`}
      colSpan={colSpan}
      rowSpan={rowSpan}
      scope={scope}
    >
      {children}
    </th>
  );
};

interface TableCellProps {
  children: ReactNode;
  className?: string;
  colSpan?: number;
  rowSpan?: number;
}

export const TableCell: React.FC<TableCellProps> = ({ children, className = '', colSpan, rowSpan }) => {
  return (
    <td className={`p-2.5 px-2 text-left align-middle ${className}`} colSpan={colSpan} rowSpan={rowSpan}>
      {children}
    </td>
  );
};
